import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    IconButton,
    InputAdornment,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import {
    Send as SendIcon,
    ArrowBack as ArrowBackIcon,
    PersonSearch as PersonSearchIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Mic as MicIcon,
    Stop as StopIcon,
    CloudOff as CloudOffIcon,
    Clear as ClearIcon,
    Draw as DrawIcon,
    AddAPhoto as AddAPhotoIcon
} from '@mui/icons-material';
import dayjs from 'dayjs';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { initializeDB } from '../../../config/db';
import { EMPLOYEE_STORE, EQUIPMENT_STORE, STATUS_DRAFT, STATUS_SUBMITTED } from '../../../config/constants';
import SelectSingleEmployeeModal from '../../shared/SelectSingleEmployeeModal';
import SignatureModalWithAuto from '../../common/SignatureModalWithAuto';
import CameraCapture from '../../shared/CameraCapture';
import useDictation from '../../../hooks/useDictation';
import { useOnlineStatus } from '../../../hooks/useOnlineStatus';
import { generateUUID } from '../../../utils/uuid';
import { parseEquipmentCategory } from '../../../utils/equipmentIdentityParser';
import { liftTruckPmService } from '../../../services/liftTruckPmService';
import {
    POWER_TYPE,
    POWER_TYPE_LABELS,
    FORM_TITLES,
    FUEL_TYPES,
    VOLTAGES,
    VOLTAGE_OTHER,
    MAX_REMARKS,
    MAX_COMMENT_LENGTH,
    MAX_PHOTOS,
    MAX_CAPTION_LENGTH,
    getSections,
    getResults,
    isItemDone,
    isLiftOneSheet,
    hasLastPm,
    usesComments,
    hasChecklist,
    defaultTechName,
    parseNumber
} from './liftTruckPmSchema';

/**
 * Lift Truck PM Inspection — одна страница, как Job Site Safety Inspection:
 * блок Inspection Information, разделы бланка, у пункта кнопки ответа и третьей
 * колонкой комментарий с надиктовкой, внизу Photos, Remarks (на JLG и Skyjack —
 * Comments) и подпись техника. Строка пункта, заголовки разделов, подсветка
 * незаполненного, блок подписи и сабмит повторяют JSSI.
 *
 * Бланк открывается сам по типу выбранной техники: LiftOne IC / Electric (погрузчики,
 * ответ OK / ADJ), JLG (телескопы и бум-лифты) и Skyjack (ножничные) — ответ OK / N/A /
 * ADJ. Тип приходит с техникой из синхронизации (сервер выводит его из внешней
 * Equiptype), руками на планшете он не выбирается.
 *
 * Фото — до 12, кнопка «Add photo» в закреплённой шапке: снять можно на любом пункте,
 * не листая наверх. Снимки с подписью и надиктовкой — блоком Photos перед Remarks, в PDF
 * по четыре на отдельной странице. Сжатие — как у Lift Evaluation.
 *
 * Номер техника здесь не вводится и не показывается — он только в PDF: подставляется
 * вместе с именем из карточки сотрудника.
 */

/**
 * Ужимаем снимок до отправки, как в Lift Evaluation: двенадцать фото с камеры айпада —
 * это десятки мегабайт в черновике IndexedDB и в запросе. 1200 px по длинной стороне
 * хватает, чтобы разобрать узел.
 */
const PHOTO_MAX_DIM = 1200;
const PHOTO_QUALITY = 0.6;

function shrinkDataUrl(dataUrl, maxDim = PHOTO_MAX_DIM, quality = PHOTO_QUALITY) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            let { width, height } = img;
            if (width > maxDim || height > maxDim) {
                const ratio = Math.min(maxDim / width, maxDim / height);
                width = Math.round(width * ratio);
                height = Math.round(height * ratio);
            }
            const canvas = document.createElement('canvas');
            canvas.width = width;
            canvas.height = height;
            canvas.getContext('2d').drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL('image/jpeg', quality));
        };
        img.onerror = reject;
        img.src = dataUrl;
    });
}

function fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => shrinkDataUrl(e.target.result).then(resolve, reject);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

const HEADER_DOM_IDS = {
    equipment: 'ltpm-hdr-equipment',
    inspectionDate: 'ltpm-hdr-date',
    hourMeter: 'ltpm-hdr-hour-meter',
    tech: 'ltpm-hdr-tech',
};

const SIGNATURE_DOM_ID = 'ltpm-signature';

/** Дата и время подписи — всегда по Нью-Йорку, где бы ни стоял планшет (как в JSSI). */
const formatSignedAt = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString('en-US', {
        timeZone: 'America/New_York',
        month: '2-digit', day: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
    });
};

/**
 * Пустой ли холст подписи. isEmpty() самого пада здесь не годится: режим Auto рисует
 * имя прямо на холсте, и пад считает его пустым. Смотрим на пиксели.
 */
const isCanvasBlank = (canvas) => {
    if (!canvas || !canvas.width || !canvas.height) return true;
    const { data } = canvas.getContext('2d').getImageData(0, 0, canvas.width, canvas.height);
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] !== 0) return false;
    }
    return true;
};

const itemDomId = (key) => `ltpm-item-${key.replace(/\./g, '-')}`;

const newRemark = (text = '') => ({ id: generateUUID(), text });

/** Черновик мог сохраниться строками — читаем оба вида. */
const normalizeRemarks = (remarks) =>
    (Array.isArray(remarks) ? remarks : []).map((remark) =>
        typeof remark === 'string' ? newRemark(remark) : { id: remark?.id || generateUUID(), text: remark?.text || '' }
    );

/** Фото: { id, path (data-URL), caption } — id нужен только планшету, чтобы строки не путались. */
const normalizePhotos = (photos) =>
    (Array.isArray(photos) ? photos : [])
        .filter((photo) => photo && photo.path)
        .map((photo) => ({ id: photo.id || generateUUID(), path: photo.path, caption: photo.caption || '' }));

/**
 * Ответы и поля шапки, которые относятся к конкретному бланку. При смене бланка их
 * сбрасываем (с подтверждением) — отметки IC-бланка не должны оказаться на электрическом.
 * Фото остаются: они к технике, а не к пунктам бланка.
 */
const CLEARED_ANSWERS = {
    checklist: {},
    itemComments: {},
    checklistPowerType: '',
    chainWearPercent: '',
    forkWearPercent: '',
    fuelType: '',
    voltage: '',
    voltageOther: '',
};

const hasSheetAnswers = (form) =>
    Object.keys(form.checklist || {}).length > 0
    || Object.values(form.itemComments || {}).some((text) => String(text || '').trim() !== '')
    || !!form.chainWearPercent || !!form.forkWearPercent
    || !!form.fuelType || !!form.voltage;

/**
 * Make / Model / Serial единицы: что уже известно справочнику, иначе — разбор
 * Category (во внешней БД Make/Model/SerialNo пустые). Без сети тоже работает.
 */
const identityOf = (unit) => {
    const identity = unit?.identity || {};
    if (identity.manufacturer || identity.model || identity.serialNo) return identity;
    return parseEquipmentCategory(unit?.category);
};

const LiftTruckPmForm = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const uuid = searchParams.get('uuid');
    const isOnline = useOnlineStatus();

    const saveTimeoutRef = useRef(null);
    const pendingSaveRef = useRef(null);

    const [units, setUnits] = useState([]);
    const [missingIds, setMissingIds] = useState(() => new Set());
    const [submitted, setSubmitted] = useState(false);
    const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [cameraOpen, setCameraOpen] = useState(false);
    const sigPadRef = useRef(null);

    // Dictation — single hook reused for all comments and remarks (as in JSSI)
    const currentFieldRef = useRef(null);
    const [activeField, setActiveField] = useState(null);
    const { isSupported: dictationSupported, isListening, start: startDictation, stop: stopDictation } = useDictation({
        lang: 'en-US', interimResults: false, continuous: false, safetyTimeoutMs: 15000, prime: true,
    });

    const [form, setForm] = useState(() => ({
        uuid: uuid || generateUUID(),
        status: STATUS_DRAFT,

        equipmentId: null,
        equipmentCode: '',
        equipmentDescription: '',
        powerType: '',

        make: '',
        model: '',
        serialNo: '',
        // Техник — залогиненный сотрудник, сменить можно выбором из списка. Номер (EE)
        // едет вместе с именем и виден только в PDF.
        techName: defaultTechName(),
        techNumber: '',
        inspectionDate: dayjs().format('YYYY-MM-DD'),
        lastPmDate: '',
        hourMeter: '',

        ...CLEARED_ANSWERS,

        photos: [],
        remarks: [newRemark()],
        followUpAssignment: '',

        // Подпись техника; signedAt — ISO (UTC) в момент нажатия Accept, сервер
        // переводит его в нью-йоркскую зону и печатает в PDF.
        techSignature: '',
        techSignedAt: null,
    }));

    // Последнее состояние — для асинхронных шагов (диалоги, ответ сервера).
    const formRef = useRef(form);
    formRef.current = form;

    // ---- Draft ------------------------------------------------------------
    const queueSave = useCallback((updated) => {
        pendingSaveRef.current = updated;
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = setTimeout(() => {
            const data = pendingSaveRef.current;
            pendingSaveRef.current = null;
            if (data) {
                liftTruckPmService.saveToIndexedDB(data).catch((error) => console.error('Error saving to IndexedDB:', error));
            }
        }, 500);
    }, []);

    // Ушли со страницы раньше, чем сработало автосохранение, — последние правки не теряем.
    useEffect(() => () => {
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        if (pendingSaveRef.current) {
            liftTruckPmService.saveToIndexedDB(pendingSaveRef.current).catch(() => {});
        }
    }, []);

    const patch = useCallback((updater) => {
        setForm((prev) => {
            const updated = typeof updater === 'function' ? updater(prev) : { ...prev, ...updater };
            queueSave(updated);
            return updated;
        });
    }, [queueSave]);

    const clearMissing = (id) => {
        setMissingIds((prev) => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    };

    // ---- Load -------------------------------------------------------------
    const loadUnits = useCallback(async () => {
        try {
            const db = await initializeDB();
            const all = await db.getAll(EQUIPMENT_STORE);
            // В списке — только техника, у которой есть бланк (тип из Equiptype или из админки).
            setUnits((all || [])
                .filter(hasChecklist)
                .sort((a, b) => (a.equipment || '').localeCompare(b.equipment || '', undefined, { numeric: true })));
        } catch (error) {
            console.error('Error loading equipment:', error);
        }
    }, []);

    useEffect(() => {
        loadUnits();
    }, [loadUnits]);

    // Tech и его номер (EE) — из карточки залогиненного сотрудника. Только для новой формы:
    // в черновике уже то, что выбрал техник. Черновик из-за этого не создаётся.
    useEffect(() => {
        if (uuid) return;
        (async () => {
            try {
                const employeeId = localStorage.getItem('employeeId') || '';
                if (!employeeId) return;
                const db = await initializeDB();
                const all = await db.getAll(EMPLOYEE_STORE);
                const me = (all || []).find((e) => String(e.EmployeeId ?? '') === employeeId);
                if (!me) return;
                setForm((prev) => (prev.techNumber ? prev : {
                    ...prev,
                    techName: prev.techName || me.FullName || me.fullName || '',
                    techNumber: me.ee ? String(me.ee) : '',
                }));
            } catch (error) {
                console.error('Error loading the signed-in employee:', error);
            }
        })();
    }, [uuid]);

    useEffect(() => {
        if (!uuid) return;
        (async () => {
            try {
                const data = await liftTruckPmService.getFromIndexedDB(uuid);
                if (data) {
                    setForm((prev) => ({
                        ...prev,
                        ...data,
                        checklist: data.checklist || {},
                        itemComments: data.itemComments || {},
                        checklistPowerType: data.checklistPowerType || (Object.keys(data.checklist || {}).length ? data.powerType : '') || '',
                        photos: normalizePhotos(data.photos),
                        remarks: normalizeRemarks(data.remarks),
                    }));
                    setSubmitted(data.status === STATUS_SUBMITTED);
                }
            } catch (error) {
                console.error('Error loading form data:', error);
            }
        })();
    }, [uuid]);

    // ---- Derived ----------------------------------------------------------
    const sections = useMemo(() => getSections(form.powerType), [form.powerType]);
    const results = useMemo(() => getResults(form.powerType), [form.powerType]);
    const liftOne = isLiftOneSheet(form.powerType);
    // На JLG и Skyjack общий блок называется Comments, на LiftOne — Remarks.
    const remarkWord = usesComments(form.powerType) ? 'Comment' : 'Remark';
    const photoCount = (form.photos || []).length;

    const stats = useMemo(() => {
        const checklist = form.checklist || {};
        const comments = form.itemComments || {};
        const items = sections.flatMap((section) => section.items);
        return {
            total: items.length,
            checked: items.filter((item) => isItemDone(checklist[item.key], comments[item.key])).length,
        };
    }, [sections, form.checklist, form.itemComments]);

    // Выбранная техника могла пропасть из справочника (списали) — черновик всё равно
    // показывает её, а не пустое поле.
    const unitOptions = useMemo(() => {
        if (!form.equipmentId || units.some((u) => u.id === form.equipmentId)) return units;
        return [...units, {
            id: form.equipmentId,
            equipment: form.equipmentCode,
            description: form.equipmentDescription,
            powerType: form.powerType,
        }];
    }, [units, form.equipmentId, form.equipmentCode, form.equipmentDescription, form.powerType]);

    const selectedUnit = useMemo(
        () => unitOptions.find((u) => u.id === form.equipmentId) || null,
        [unitOptions, form.equipmentId]
    );

    // ---- Dictation --------------------------------------------------------
    const toggleDictation = async (fieldId, apply) => {
        if (!dictationSupported) return;
        if (isListening && activeField === fieldId) {
            stopDictation();
            setActiveField(null);
            currentFieldRef.current = null;
            return;
        }
        if (isListening) stopDictation();
        currentFieldRef.current = fieldId;
        setActiveField(fieldId);
        await startDictation({
            onResult: (transcript) => { if (transcript && currentFieldRef.current === fieldId) apply(transcript); },
            onEnd: () => { setActiveField(null); currentFieldRef.current = null; },
            onError: () => { setActiveField(null); currentFieldRef.current = null; },
        });
    };

    // ---- Equipment and checklist type --------------------------------------
    /** Отметки сделаны на другом бланке, чем type? */
    const answersToClear = (type) => {
        const current = formRef.current;
        if (!type || !hasSheetAnswers(current)) return false;
        const answersType = current.checklistPowerType || current.powerType;
        return !!answersType && answersType !== type;
    };

    const confirmClear = async (unitLabel, type) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: 'Different checklist',
            html: `${unitLabel} uses the <b>${POWER_TYPE_LABELS[type]}</b> checklist.<br>Switching clears the answers and comments already entered.`,
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#6c757d',
            confirmButtonText: 'Switch and clear',
        });
        return result.isConfirmed;
    };

    const handleUnitChange = async (event, unit) => {
        if (!unit) {
            patch({ equipmentId: null, equipmentCode: '', equipmentDescription: '' });
            return;
        }

        // Бланк нужен ДО смены техники: только зная его, можно честно сказать,
        // пропадут ли уже сделанные отметки. Он приходит с техникой из синхронизации
        // (в списке только техника с бланком), без запросов к API.
        const type = unit.powerType || '';
        const lastPm = unit.lastPmInspectionDate || '';

        const clear = answersToClear(type);
        if (clear && !(await confirmClear(unit.equipment || 'This unit', type))) return;

        // Make / Model / Serial — сразу при выборе, из справочника или из Category.
        const identity = identityOf(unit);
        patch((prev) => ({
            ...prev,
            equipmentId: unit.id,
            equipmentCode: unit.equipment || '',
            equipmentDescription: unit.description || '',
            make: identity.manufacturer || '',
            model: identity.model || '',
            serialNo: identity.serialNo || '',
            lastPmDate: lastPm,
            powerType: type,
            ...(clear ? CLEARED_ANSWERS : {}),
        }));
        clearMissing(HEADER_DOM_IDS.equipment);
    };

    /**
     * То, что техник вписал или поправил в Make / Model / Serial, запоминаем для
     * единицы — в следующий раз подставится из справочника. Молча: это удобство.
     */
    const rememberIdentityIfNeeded = async () => {
        const unit = units.find((u) => u.id === form.equipmentId);
        if (!unit) return;

        const identity = unit.identity || {};
        if (identity.source === 'external') return;
        if (!form.make && !form.model && !form.serialNo) return;

        const changed =
            (form.make || '') !== (identity.manufacturer || '') ||
            (form.model || '') !== (identity.model || '') ||
            (form.serialNo || '') !== (identity.serialNo || '');
        if (!changed) return;

        try {
            await liftTruckPmService.saveEquipmentIdentity(unit.id, {
                manufacturer: form.make,
                model: form.model,
                serialNo: form.serialNo,
            });
        } catch (error) {
            console.error('Could not remember equipment identity:', error);
        }
    };

    // ---- Answers ----------------------------------------------------------
    const setHeader = (field, value, domId) => {
        patch({ [field]: value });
        if (domId) clearMissing(domId);
    };

    const setResult = (key, value) => {
        patch((prev) => ({
            ...prev,
            checklist: { ...(prev.checklist || {}), [key]: value },
            checklistPowerType: prev.powerType,
        }));
        clearMissing(itemDomId(key));
    };

    const setComment = (key, text) => {
        patch((prev) => ({
            ...prev,
            itemComments: { ...(prev.itemComments || {}), [key]: text },
            checklistPowerType: prev.powerType,
        }));
        if (text.trim()) clearMissing(itemDomId(key));
    };

    const appendComment = (key, transcript) => {
        patch((prev) => {
            const cur = prev.itemComments?.[key] || '';
            return {
                ...prev,
                itemComments: { ...(prev.itemComments || {}), [key]: cur + (cur ? ' ' : '') + transcript },
                checklistPowerType: prev.powerType,
            };
        });
        clearMissing(itemDomId(key));
    };

    const setPercent = (field, itemKey, value) => {
        patch((prev) => ({ ...prev, [field]: value, checklistPowerType: prev.powerType }));
        clearMissing(`${itemDomId(itemKey)}-percent`);
    };

    // ---- Tech and signature -----------------------------------------------
    const handleTechSelect = (employee) => {
        setEmployeePickerOpen(false);
        const fullName = employee?.FullName || employee?.fullName || '';
        if (!fullName) return;
        const number = employee?.ee ? String(employee.ee) : '';
        patch((prev) => {
            // Выбрали другого человека — прежний росчерк уже не его (как смена прораба
            // в JSSI): иначе под новым именем в PDF остался бы чужой росчерк.
            const samePerson = prev.techName === fullName
                && (!prev.techNumber || !number || prev.techNumber === number);
            return {
                ...prev,
                techName: fullName,
                techNumber: number,
                ...(samePerson ? {} : { techSignature: '', techSignedAt: null }),
            };
        });
        clearMissing(HEADER_DOM_IDS.tech);
    };

    const handleSignatureAccept = () => {
        const canvas = sigPadRef.current?.getCanvas();
        if (!canvas || isCanvasBlank(canvas)) {
            Swal.fire({
                icon: 'info',
                title: 'Signature',
                text: 'Please sign before accepting.',
                // Окно подписи — MUI Modal (z-index 1300), Swal по умолчанию 1060 и ушёл бы под него.
                didOpen: () => {
                    document.querySelectorAll('.swal2-container').forEach((c) => { c.style.zIndex = '13000'; });
                },
            });
            return;
        }
        patch({ techSignature: sigPadRef.current.toDataURL(), techSignedAt: new Date().toISOString() });
        clearMissing(SIGNATURE_DOM_ID);
        setSignatureModalOpen(false);
    };

    const handleClearSignature = () => patch({ techSignature: '', techSignedAt: null });

    // ---- Remarks ----------------------------------------------------------
    const addRemark = () => {
        if ((form.remarks || []).length >= MAX_REMARKS) {
            Swal.fire(`${remarkWord} limit reached`, `You can add up to ${MAX_REMARKS} ${remarkWord.toLowerCase()}s.`, 'info');
            return;
        }
        patch((prev) => ({ ...prev, remarks: [...(prev.remarks || []), newRemark()] }));
    };

    const updateRemark = (id, text) => {
        patch((prev) => ({
            ...prev,
            remarks: (prev.remarks || []).map((remark) => (remark.id === id ? { ...remark, text } : remark)),
        }));
    };

    const appendRemark = (id, transcript) => {
        patch((prev) => ({
            ...prev,
            remarks: (prev.remarks || []).map((remark) => (
                remark.id === id ? { ...remark, text: (remark.text || '') + (remark.text ? ' ' : '') + transcript } : remark
            )),
        }));
    };

    const removeRemark = async (remark) => {
        if (remark.text?.trim()) {
            const result = await Swal.fire({
                title: `Remove this ${remarkWord.toLowerCase()}?`,
                text: remark.text.length > 120 ? `${remark.text.slice(0, 120)}…` : remark.text,
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#6c757d',
                confirmButtonText: 'Yes, remove it',
            });
            if (!result.isConfirmed) return;
        }
        if (activeField === `rmk:${remark.id}`) stopDictation();
        patch((prev) => ({ ...prev, remarks: (prev.remarks || []).filter((r) => r.id !== remark.id) }));
    };

    // ---- Follow-up Assignment ---------------------------------------------
    const appendFollowUp = (transcript) => {
        patch((prev) => {
            const cur = prev.followUpAssignment || '';
            return { ...prev, followUpAssignment: cur + (cur ? ' ' : '') + transcript };
        });
    };

    // ---- Photos -----------------------------------------------------------
    const openCamera = () => {
        if (photoCount >= MAX_PHOTOS) {
            Swal.fire({ icon: 'info', title: 'Photo limit reached', text: `You can attach up to ${MAX_PHOTOS} photos.` });
            return;
        }
        setCameraOpen(true);
    };

    const handlePhotoCapture = async (payload) => {
        setCameraOpen(false);
        if (!payload) return;
        if ((formRef.current.photos || []).length >= MAX_PHOTOS) {
            Swal.fire({ icon: 'info', title: 'Photo limit reached', text: `You can attach up to ${MAX_PHOTOS} photos.` });
            return;
        }

        try {
            const dataUrl = payload.file ? await fileToDataUrl(payload.file) : await shrinkDataUrl(payload.dataUrl);
            patch((prev) => ({
                ...prev,
                photos: [...(prev.photos || []), { id: generateUUID(), path: dataUrl, caption: '' }].slice(0, MAX_PHOTOS),
            }));
        } catch (error) {
            console.error('Photo error:', error);
            Swal.fire({ icon: 'error', title: 'Error', text: 'Could not attach the photo' });
        }
    };

    const removePhoto = (id) => {
        if (activeField === `photo:${id}`) stopDictation();
        patch((prev) => ({ ...prev, photos: (prev.photos || []).filter((photo) => photo.id !== id) }));
    };

    const updatePhotoCaption = (id, caption) => {
        patch((prev) => ({
            ...prev,
            photos: (prev.photos || []).map((photo) => (photo.id === id ? { ...photo, caption } : photo)),
        }));
    };

    const appendPhotoCaption = (id, transcript) => {
        patch((prev) => ({
            ...prev,
            photos: (prev.photos || []).map((photo) => (
                photo.id === id
                    ? { ...photo, caption: ((photo.caption || '') + (photo.caption ? ' ' : '') + transcript).slice(0, MAX_CAPTION_LENGTH) }
                    : photo
            )),
        }));
    };

    // ---- Submit -----------------------------------------------------------
    /** Незаполненное обязательное — как getMissingRequired у JSSI: {id, label}. */
    const getMissingRequired = () => {
        const missing = [];

        if (!form.equipmentCode) {
            missing.push({ id: HEADER_DOM_IDS.equipment, label: 'Equipment' });
        } else if (!form.powerType) {
            missing.push({ id: HEADER_DOM_IDS.equipment, label: 'Checklist type — select the equipment again' });
        }
        if (!form.inspectionDate) missing.push({ id: HEADER_DOM_IDS.inspectionDate, label: 'Date' });
        // Hour Meter обязателен на LiftOne (там он со звёздочкой); на JLG и Skyjack — нет,
        // но вписанное должно быть числом.
        const hourMeterRaw = String(form.hourMeter ?? '').trim();
        const hourMeter = parseNumber(hourMeterRaw);
        if (liftOne && (hourMeter === null || hourMeter < 0)) {
            missing.push({ id: HEADER_DOM_IDS.hourMeter, label: 'Hour Meter' });
        } else if (!liftOne && hourMeterRaw !== '' && (hourMeter === null || hourMeter < 0)) {
            missing.push({ id: HEADER_DOM_IDS.hourMeter, label: 'Hourmeter Reading — must be a number' });
        }
        if (!String(form.techName || '').trim()) missing.push({ id: HEADER_DOM_IDS.tech, label: 'Tech' });

        const checklist = form.checklist || {};
        const comments = form.itemComments || {};
        for (const section of sections) {
            for (const item of section.items) {
                if (!isItemDone(checklist[item.key], comments[item.key])) {
                    missing.push({ id: itemDomId(item.key), label: `${section.title}: ${item.label}` });
                }
                if (item.percentField) {
                    const raw = form[item.percentField];
                    if (raw !== '' && raw !== null && raw !== undefined) {
                        const value = parseNumber(raw);
                        if (value === null || value < 0 || value > 100) {
                            missing.push({ id: `${itemDomId(item.key)}-percent`, label: `${item.label} — wear must be 0–100%` });
                        }
                    }
                }
            }
        }

        if (!form.techSignature) missing.push({ id: SIGNATURE_DOM_ID, label: 'Tech signature' });

        return missing;
    };

    const serverFieldLabel = (field) => {
        if (field.startsWith('item:')) {
            const key = field.slice('item:'.length);
            const item = sections.flatMap((s) => s.items).find((i) => i.key === key);
            return item ? item.label : key;
        }
        return {
            equipmentCode: 'Equipment',
            powerType: 'Checklist type',
            inspectionDate: 'Date',
            hourMeter: 'Hour Meter',
            techName: 'Tech',
            techSignature: 'Tech signature',
        }[field] || field;
    };

    const handleSubmit = async () => {
        const missing = getMissingRequired();
        if (missing.length > 0) {
            setMissingIds(new Set(missing.map((m) => m.id)));
            const list = missing.slice(0, 25).map((m) => `• ${m.label}`).join('<br>');
            const more = missing.length > 25 ? `<br>…and ${missing.length - 25} more` : '';
            await Swal.fire({
                title: `Please complete ${missing.length} required item${missing.length > 1 ? 's' : ''}`,
                html: `<div style="text-align:left;max-height:50vh;overflow:auto;font-size:0.85rem">${list}${more}</div>`,
                icon: 'warning',
            });
            // Scroll to the first unanswered item once the popup is dismissed.
            const el = document.getElementById(missing[0].id);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
        }
        setMissingIds(new Set());

        const result = await Swal.fire({
            title: 'Submit Inspection?',
            text: 'Once submitted, you cannot edit this inspection.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, submit it!',
        });
        if (!result.isConfirmed) return;

        // Автосохранение, сработавшее после удаления, вернуло бы отправленную форму в черновики.
        if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
        pendingSaveRef.current = null;

        try {
            await rememberIdentityIfNeeded();
            const response = await liftTruckPmService.submit({
                ...form,
                remarks: (form.remarks || []).map((remark) => remark.text).filter((text) => text && text.trim()),
                photos: (form.photos || []).map((photo) => ({ path: photo.path, caption: (photo.caption || '').trim() })),
                status: STATUS_SUBMITTED,
            });
            if (!response.success) {
                throw new Error(response.message || 'Submission failed');
            }
            await liftTruckPmService.deleteFromIndexedDB(form.uuid);
            Swal.fire('Success', 'Inspection submitted successfully!', 'success');
            navigate('/operations/lift-truck-pm');
        } catch (e) {
            console.error('Error submitting:', e);
            const serverMissing = e.response?.data?.missing;
            const message = serverMissing?.length
                ? `Missing: ${serverMissing.map(serverFieldLabel).join(', ')}`
                : (e.response?.data?.message || e.message || 'Unknown error');
            Swal.fire('Error', 'Failed to submit inspection: ' + message, 'error');
        }
    };

    // ---- Renderers --------------------------------------------------------

    /** Строка пункта — как renderChoiceRow у JSSI: название | OK / ADJ | комментарий с надиктовкой. */
    const renderItemRow = (item) => {
        const value = form.checklist?.[item.key];
        const comment = form.itemComments?.[item.key] || '';
        const fieldId = `cmt:${item.key}`;
        const listening = isListening && activeField === fieldId;
        const isMissing = missingIds.has(itemDomId(item.key));
        const percentMissing = missingIds.has(`${itemDomId(item.key)}-percent`);

        return (
            <Box key={item.key} id={itemDomId(item.key)} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 1.5, pb: 1.25, borderBottom: '1px solid #eee', borderLeft: isMissing ? '3px solid #f44336' : '3px solid transparent', bgcolor: isMissing ? 'rgba(244,67,54,0.08)' : 'transparent', pl: 1, borderRadius: 1, scrollMarginTop: '80px' }}>
                <Box sx={{ flex: '0 0 38%', minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.4 }}>
                        <span style={{ color: '#d32f2f' }}>* </span>
                        {item.label}
                    </Typography>
                </Box>
                <Box sx={{ flex: '0 0 auto' }}>
                    <Stack direction="row" spacing={0.5}>
                        {results.map((option) => (
                            <Button
                                key={option.key}
                                variant={value === option.key ? 'contained' : 'outlined'}
                                color={option.color}
                                size="small"
                                onClick={() => setResult(item.key, option.key)}
                                disabled={submitted}
                                sx={{ minWidth: 42, height: 32, fontSize: '0.7rem', p: '4px 8px' }}
                            >
                                {option.label}
                            </Button>
                        ))}
                    </Stack>
                    {item.percentField && (
                        <TextField
                            id={`${itemDomId(item.key)}-percent`}
                            size="small"
                            placeholder="Wear"
                            value={form[item.percentField] || ''}
                            onChange={(e) => setPercent(item.percentField, item.key, e.target.value)}
                            disabled={submitted}
                            error={percentMissing}
                            inputProps={{ inputMode: 'decimal' }}
                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                            sx={{ mt: 0.5, width: 100, scrollMarginTop: '80px', '& .MuiInputBase-input': { fontSize: '0.75rem', p: '4px 6px' } }}
                        />
                    )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <TextField
                        fullWidth multiline size="small"
                        placeholder="Comment"
                        value={comment}
                        onChange={(e) => setComment(item.key, e.target.value)}
                        disabled={submitted}
                        inputProps={{ maxLength: MAX_COMMENT_LENGTH }}
                        InputProps={{
                            endAdornment: !submitted && dictationSupported && (
                                <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.2 }}>
                                    <IconButton size="small" onClick={() => toggleDictation(fieldId, (t) => appendComment(item.key, t))} color={listening ? 'error' : 'primary'}>
                                        {listening ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ '& .MuiInputBase-input': { fontSize: '0.75rem', p: '3px 6px' }, '& .MuiInputBase-root': { alignItems: 'flex-start', p: 0 }, '& .MuiOutlinedInput-root': { p: 0 } }}
                    />
                </Box>
            </Box>
        );
    };

    /** Ремарка — как renderTextRow у JSSI: подпись и многострочное поле с надиктовкой. */
    const renderRemark = (remark, index) => {
        const fieldId = `rmk:${remark.id}`;
        const listening = isListening && activeField === fieldId;
        return (
            <Box key={remark.id} sx={{ mb: 1.5, pl: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{remarkWord} {index + 1}</Typography>
                    {!submitted && (
                        <IconButton size="small" onClick={() => removeRemark(remark)} sx={{ ml: 'auto' }} title={`Remove this ${remarkWord.toLowerCase()}`}>
                            <DeleteIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>
                <TextField
                    fullWidth multiline minRows={2} size="small"
                    value={remark.text || ''}
                    onChange={(e) => updateRemark(remark.id, e.target.value)}
                    disabled={submitted}
                    inputProps={{ maxLength: 4000 }}
                    InputProps={{
                        endAdornment: !submitted && dictationSupported && (
                            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                                <IconButton size="small" onClick={() => toggleDictation(fieldId, (t) => appendRemark(remark.id, t))} color={listening ? 'error' : 'primary'}>
                                    {listening ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                </IconButton>
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
        );
    };

    /**
     * Блок подписи — как renderSignatureBox у JSSI: кто подписывает, росчерк
     * (тап — переподписать, × — стереть) и когда расписались, по Нью-Йорку.
     */
    const renderSignatureBox = () => {
        const isMissing = missingIds.has(SIGNATURE_DOM_ID);
        const signedAtLabel = formatSignedAt(form.techSignedAt);
        const blocked = !String(form.techName || '').trim() && !form.techSignature;

        return (
            <Box
                id={SIGNATURE_DOM_ID}
                sx={{
                    scrollMarginTop: '80px',
                    border: isMissing ? '1px solid #f44336' : '1px solid #e0e0e0',
                    borderRadius: 1,
                    p: 1.5,
                    height: '100%',
                    // Иначе рамка выше ячейки на отступы и бордер, и карточка срезает её низ.
                    boxSizing: 'border-box',
                }}
            >
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Tech Signature *</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                    {form.techName || '—'}
                </Typography>

                {form.techSignature ? (
                    <Box position="relative" display="inline-block">
                        <img
                            src={form.techSignature} alt="Tech signature"
                            style={{ maxWidth: 200, height: 60, border: '1px solid #ddd', borderRadius: 4, cursor: submitted ? 'default' : 'pointer' }}
                            onClick={() => { if (!submitted) setSignatureModalOpen(true); }}
                        />
                        {!submitted && (
                            <IconButton size="small" onClick={handleClearSignature} sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}>
                                <ClearIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                        )}
                    </Box>
                ) : (
                    <Button
                        variant="outlined" startIcon={<DrawIcon />} size="small"
                        onClick={() => setSignatureModalOpen(true)} disabled={submitted || blocked}
                    >
                        Add Signature
                    </Button>
                )}

                <Typography
                    variant="caption"
                    color={blocked ? 'error.main' : 'text.secondary'}
                    sx={{ display: 'block', mt: 1 }}
                >
                    {blocked
                        ? 'Select the tech in the header first'
                        : (signedAtLabel ? `Signed: ${signedAtLabel} (ET)` : 'Not signed yet')}
                </Typography>
            </Box>
        );
    };

    return (
        <Box sx={{ height: 'calc(100vh - 60px)', backgroundColor: '#f5f5f5', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Header */}
            <Box sx={{ backgroundColor: 'white', borderBottom: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <IconButton onClick={() => navigate('/operations/lift-truck-pm')} edge="start">
                            <ArrowBackIcon />
                        </IconButton>
                        <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6" fontWeight={600} lineHeight={1.2}>LIFT TRUCK PM INSPECTION</Typography>
                            {form.powerType && (
                                <Typography variant="caption" color="text.secondary">{FORM_TITLES[form.powerType]}</Typography>
                            )}
                        </Box>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                        {/* Фото — из закреплённой шапки: снять можно на любом пункте, не листая наверх. */}
                        {!submitted && (
                            <Button
                                size="small" variant="outlined" startIcon={<AddAPhotoIcon />}
                                onClick={openCamera} disabled={photoCount >= MAX_PHOTOS}
                                sx={{ whiteSpace: 'nowrap' }}
                            >
                                Add photo {photoCount}/{MAX_PHOTOS}
                            </Button>
                        )}
                        {stats.total > 0 && (
                            <Chip label={`Checked: ${stats.checked}/${stats.total}`} size="small" color="primary" variant="outlined" />
                        )}
                        {form.powerType && <Chip label={POWER_TYPE_LABELS[form.powerType]} size="small" variant="outlined" />}
                        {!isOnline && <Chip icon={<CloudOffIcon />} label="Offline" size="small" />}
                        <Chip label={submitted ? 'Submitted' : 'Draft'} size="small" sx={{ backgroundColor: submitted ? '#4caf50' : '#9e9e9e', color: 'white' }} />
                    </Stack>
                </Box>
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
                    {/* Inspection Information */}
                    <Card elevation={1} sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1.5, color: 'primary.main' }}>Inspection Information</Typography>
                            <Grid container rowSpacing={1.5} columnSpacing={1.5}>
                                <Grid item xs={12} sm={6} md={4} id={HEADER_DOM_IDS.equipment} sx={{ scrollMarginTop: '80px' }}>
                                    <Autocomplete
                                        options={unitOptions}
                                        getOptionLabel={(option) => `${option.equipment || ''} — ${option.description || ''}`}
                                        isOptionEqualToValue={(option, value) => option.id === value?.id}
                                        value={selectedUnit}
                                        onChange={handleUnitChange}
                                        disabled={submitted}
                                        renderInput={(params) => (
                                            <TextField {...params} size="small" label="Equipment *" placeholder="Select equipment" error={missingIds.has(HEADER_DOM_IDS.equipment)} />
                                        )}
                                        noOptionsText="No equipment with a checklist type on this device — run Synchronization"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} id={HEADER_DOM_IDS.inspectionDate} sx={{ scrollMarginTop: '80px' }}>
                                    <TextField
                                        fullWidth size="small" label="Date *" type="date"
                                        value={form.inspectionDate}
                                        onChange={(e) => setHeader('inspectionDate', e.target.value, HEADER_DOM_IDS.inspectionDate)}
                                        disabled={submitted}
                                        InputLabelProps={{ shrink: true }}
                                        error={missingIds.has(HEADER_DOM_IDS.inspectionDate)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4} id={HEADER_DOM_IDS.hourMeter} sx={{ scrollMarginTop: '80px' }}>
                                    <TextField
                                        fullWidth size="small"
                                        label={liftOne ? 'Hour Meter *' : (form.powerType ? 'Hourmeter Reading' : 'Hour Meter')}
                                        value={form.hourMeter}
                                        onChange={(e) => setHeader('hourMeter', e.target.value, HEADER_DOM_IDS.hourMeter)}
                                        disabled={submitted}
                                        inputProps={{ inputMode: 'decimal' }}
                                        error={missingIds.has(HEADER_DOM_IDS.hourMeter)}
                                    />
                                </Grid>
                                {/* У ножничного и бум-лифта Last PM нет — у них программа Annual. */}
                                {hasLastPm(form.powerType) && (
                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField
                                            fullWidth size="small" label="Last PM (Equip Docs)"
                                            value={form.lastPmDate ? dayjs(form.lastPmDate).format('MM/DD/YYYY') : ''}
                                            placeholder={form.equipmentId ? 'No PM on record' : ''}
                                            InputLabelProps={{ shrink: true }}
                                            InputProps={{ readOnly: true }}
                                            disabled={submitted}
                                        />
                                    </Grid>
                                )}
                                {/* Make есть только на LiftOne; на JLG и Skyjack — Model и Serial Number. */}
                                {(liftOne || !form.powerType) && (
                                    <Grid item xs={12} sm={6} md={4}>
                                        <TextField fullWidth size="small" label="Make" value={form.make}
                                            onChange={(e) => setHeader('make', e.target.value)} disabled={submitted} />
                                    </Grid>
                                )}
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField fullWidth size="small"
                                        label={form.powerType === POWER_TYPE.TELEHANDLER || form.powerType === POWER_TYPE.BOOM ? 'Machine Model' : 'Model'}
                                        value={form.model}
                                        onChange={(e) => setHeader('model', e.target.value)} disabled={submitted} />
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <TextField fullWidth size="small"
                                        label={liftOne || !form.powerType ? 'Serial No.' : 'Serial Number'}
                                        value={form.serialNo}
                                        onChange={(e) => setHeader('serialNo', e.target.value)} disabled={submitted} />
                                </Grid>
                                {/* Техник выбирается из сотрудников, а не впечатывается: его номер
                                    не показан на форме и должен совпадать с именем. */}
                                <Grid item xs={12} sm={6} md={4} id={HEADER_DOM_IDS.tech} sx={{ scrollMarginTop: '80px' }}>
                                    <TextField
                                        fullWidth size="small" label="Tech *"
                                        value={form.techName}
                                        placeholder="Select employee"
                                        onClick={() => !submitted && setEmployeePickerOpen(true)}
                                        disabled={submitted}
                                        error={missingIds.has(HEADER_DOM_IDS.tech)}
                                        InputLabelProps={{ shrink: true }}
                                        InputProps={{
                                            readOnly: true,
                                            style: { cursor: submitted ? 'default' : 'pointer' },
                                            endAdornment: !submitted && (
                                                <InputAdornment position="end">
                                                    <IconButton size="small" onClick={() => setEmployeePickerOpen(true)}><PersonSearchIcon fontSize="small" /></IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>

                                {form.powerType === POWER_TYPE.IC && (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Fuel</Typography>
                                        <ToggleButtonGroup
                                            color="primary"
                                            value={form.fuelType || null}
                                            exclusive
                                            onChange={(e, newValue) => patch((prev) => ({ ...prev, fuelType: newValue || '', checklistPowerType: prev.powerType }))}
                                            disabled={submitted}
                                            size="small"
                                        >
                                            {FUEL_TYPES.map((fuel) => (
                                                <ToggleButton key={fuel.key} value={fuel.key} sx={{ px: 3 }}>{fuel.label}</ToggleButton>
                                            ))}
                                        </ToggleButtonGroup>
                                    </Grid>
                                )}

                                {form.powerType === POWER_TYPE.ELECTRIC && (
                                    <Grid item xs={12}>
                                        <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>Voltage</Typography>
                                        <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
                                            <ToggleButtonGroup
                                                color="primary"
                                                value={form.voltage || null}
                                                exclusive
                                                onChange={(e, newValue) => patch((prev) => ({
                                                    ...prev,
                                                    voltage: newValue || '',
                                                    ...(newValue === VOLTAGE_OTHER ? {} : { voltageOther: '' }),
                                                    checklistPowerType: prev.powerType,
                                                }))}
                                                disabled={submitted}
                                                size="small"
                                            >
                                                {VOLTAGES.map((voltage) => (
                                                    <ToggleButton key={voltage.key} value={voltage.key} sx={{ px: 3 }}>{voltage.label}</ToggleButton>
                                                ))}
                                            </ToggleButtonGroup>
                                            {form.voltage === VOLTAGE_OTHER && (
                                                <TextField
                                                    size="small" label="Other voltage"
                                                    value={form.voltageOther}
                                                    onChange={(e) => setHeader('voltageOther', e.target.value)}
                                                    disabled={submitted}
                                                    inputProps={{ maxLength: 50 }}
                                                />
                                            )}
                                        </Stack>
                                    </Grid>
                                )}

                            </Grid>
                        </CardContent>
                    </Card>

                    {!form.powerType && (
                        <Card elevation={1} sx={{ mb: 2 }}>
                            <CardContent>
                                <Typography variant="body2" color="text.secondary">
                                    Select the equipment — the checklist for its type opens automatically.
                                </Typography>
                            </CardContent>
                        </Card>
                    )}

                    {/* Sections */}
                    {sections.map((section) => (
                        <Box key={section.key} sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem', mb: 1, color: 'primary.main', borderBottom: '2px solid', borderColor: 'primary.light', pb: 0.5 }}>
                                {section.title}
                                {section.subtitle && (
                                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                        {section.subtitle}
                                    </Typography>
                                )}
                            </Typography>
                            {section.items.map(renderItemRow)}
                        </Box>
                    ))}

                    {/* Photos — снимки из кнопки в шапке, у каждого подпись с надиктовкой (как в Lift Evaluation) */}
                    {photoCount > 0 && (
                        <Card elevation={1} sx={{ mb: 2 }}>
                            <CardContent sx={{ py: 1.75 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Photos</Typography>
                                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{photoCount} of {MAX_PHOTOS}</Typography>
                                </Box>
                                <Grid container spacing={2}>
                                    {form.photos.map((photo, index) => {
                                        const fieldId = `photo:${photo.id}`;
                                        const listening = isListening && activeField === fieldId;
                                        return (
                                            <Grid item xs={12} sm={6} md={4} key={photo.id}>
                                                <Box sx={{ position: 'relative' }}>
                                                    <Box component="img" src={photo.path} alt={`Photo ${index + 1}`}
                                                        sx={{ width: '100%', borderRadius: 1, border: '1px solid #ddd', display: 'block' }} />
                                                    {!submitted && (
                                                        <IconButton size="small" onClick={() => removePhoto(photo.id)} title="Remove this photo"
                                                            sx={{ position: 'absolute', top: -8, right: -8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}>
                                                            <ClearIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                                <TextField
                                                    fullWidth multiline size="small"
                                                    label={`Photo ${index + 1} caption`}
                                                    value={photo.caption || ''}
                                                    onChange={(e) => updatePhotoCaption(photo.id, e.target.value)}
                                                    disabled={submitted}
                                                    inputProps={{ maxLength: MAX_CAPTION_LENGTH }}
                                                    InputProps={{
                                                        endAdornment: !submitted && dictationSupported && (
                                                            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                                                                <IconButton size="small" onClick={() => toggleDictation(fieldId, (t) => appendPhotoCaption(photo.id, t))} color={listening ? 'error' : 'primary'}>
                                                                    {listening ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                                </IconButton>
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                    sx={{ mt: 1 }}
                                                />
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            </CardContent>
                        </Card>
                    )}

                    {/* Remarks */}
                    <Card elevation={1} sx={{ mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{remarkWord}s</Typography>
                                {!submitted && (
                                    <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={addRemark} sx={{ ml: 'auto' }}>
                                        Add {remarkWord}
                                    </Button>
                                )}
                            </Box>
                            {(form.remarks || []).length === 0 ? (
                                <Typography variant="caption" color="text.secondary">No {remarkWord.toLowerCase()}s.</Typography>
                            ) : (
                                (form.remarks || []).map(renderRemark)
                            )}
                        </CardContent>
                    </Card>

                    {/* Follow-up Assignment — как ремарка, но одно поле без «+ Add». Заполненное
                        после сабмита заводит задачу инспектору во внешней Tasks (текст — в Description). */}
                    <Card elevation={1} sx={{ mt: 2, mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Follow-up Assignment</Typography>
                            <Box sx={{ pl: 1 }}>
                                <TextField
                                    fullWidth multiline minRows={2} size="small"
                                    value={form.followUpAssignment || ''}
                                    onChange={(e) => patch({ followUpAssignment: e.target.value })}
                                    disabled={submitted}
                                    InputProps={{
                                        endAdornment: !submitted && dictationSupported && (
                                            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 0.5 }}>
                                                <IconButton size="small" onClick={() => toggleDictation('followup', appendFollowUp)} color={isListening && activeField === 'followup' ? 'error' : 'primary'}>
                                                    {isListening && activeField === 'followup' ? <StopIcon fontSize="small" /> : <MicIcon fontSize="small" />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Signature — техник из шапки; время подписи попадает в PDF */}
                    <Card elevation={1} sx={{ mt: 2, mb: 0.5 }}>
                        <CardContent sx={{ py: 1.75 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>Signature</Typography>
                            <Grid container spacing={2}>
                                <Grid item xs={12} md={6}>
                                    {renderSignatureBox()}
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Box>
            </Box>

            {/* Footer */}
            <Box sx={{ backgroundColor: 'white', borderTop: '1px solid #e0e0e0', p: 1.5, flexShrink: 0 }}>
                <Box sx={{ maxWidth: 1200, mx: 'auto', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button variant="outlined" onClick={() => navigate('/operations/lift-truck-pm')}>Cancel</Button>
                    {!submitted && (
                        <Button variant="contained" startIcon={<SendIcon />} onClick={handleSubmit} sx={{ backgroundColor: '#4caf50', '&:hover': { backgroundColor: '#45a049' } }}>Submit</Button>
                    )}
                </Box>
            </Box>

            <SelectSingleEmployeeModal
                open={employeePickerOpen}
                onClose={() => setEmployeePickerOpen(false)}
                onSelect={handleTechSelect}
                title="Select Tech"
            />

            <CameraCapture
                open={cameraOpen}
                onClose={() => setCameraOpen(false)}
                onCapture={handlePhotoCapture}
            />

            {signatureModalOpen && (
                <SignatureModalWithAuto
                    open={signatureModalOpen}
                    onClose={() => setSignatureModalOpen(false)}
                    sigPadRef={sigPadRef}
                    employeeName={form.techName}
                    onAccept={handleSignatureAccept}
                    existingSignature={form.techSignature || null}
                />
            )}
        </Box>
    );
};

export default LiftTruckPmForm;
